import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchFloorFinder } from "../redux/FloorMatcherSlice";

const filterMap = {
  0: "room_filter",
  1: "size_filter",
  2: "color_filter",
  3: "pattern_filter",
};

const useFloorFinderData = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.floorFinder);
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({});
  const [steps, setSteps] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState([]);

  /* ---------------------------
     Initial API Call
  ---------------------------- */
  useEffect(() => {
    dispatch(fetchFloorFinder([]));
  }, [dispatch]);

  /* ---------------------------
     Build Steps
  ---------------------------- */
  useEffect(() => {
    if (data?.data?.carpet_finders?.length > 0) {
      const carpet = data.data.carpet_finders[0];

      setSteps([
        {
          title: "Where will your new floor covering be placed?",
          options: carpet.room_list?.map((item) => ({
            label: item?.title,
            image: item?.media,
            key: item?.room_filter,
            key_filter: item?.room_filter,
          })),
        },
        {
          title: "Which Size or Shape fits your Space?",
          options: carpet.sizes?.map((item) => ({
            label: item?.size,
            key: item?.size_filter,
            key_filter: item?.size_filter_value,
          })),
        },
        {
          title: "Which Colors complement your Space?",
          options: carpet.colors?.map((item) => ({
            label: item?.color,
            image: item?.media,
            key: item?.color_filter,
            key_filter: item?.color_filter_value,
          })),
        },
        {
          title: "What Style suits your Space best?",
          options: carpet.patterns?.map((item) => ({
            label: item?.pattern,
            image: item?.media,
            key: item?.pattern_filter,
            key_filter: item?.pattern_filter_value,
          })),
        },
      ]);
    }
  }, [data]);

  /* ---------------------------
     API Call on Filter Change
  ---------------------------- */
  useEffect(() => {
    dispatch(fetchFloorFinder(selectedFilter));
  }, [selectedFilter, dispatch]);

  /* ---------------------------
     SELECT OPTION
  ---------------------------- */
  const toggleOption = (stepIndex, label, key, key_filter) => {
    // STEP 0 (ROOM) → RESET FLOW
    if (stepIndex === 0) {
      const newSelection = [{ label, key, key_filter }];

      setSelections({
        0: newSelection,
      });

      setSelectedFilter([
        {
          [filterMap[0]]: [key],
        },
      ]);

      setTimeout(() => {
        setCurrentStep(1);
      }, 100);

      return;
    }

    // OTHER STEPS
    setSelections((prev) => {
      const current = prev[stepIndex] || [];
      const exists = current.some((item) => item.label === label);
      const updated = exists
        ? current.filter((item) => item.label !== label)
        : [...current, { label, key, key_filter }];

      return { ...prev, [stepIndex]: updated };
    });
  };

  /* ---------------------------
     NEXT STEP FILTER
  ---------------------------- */
  const getNextStepData = (step) => {
    const keyName = filterMap[step];
    const values = selections?.[step]?.map((item) => item.key);

    if (!keyName || !values?.length) return;

    setSelectedFilter((prev) => {
      const filtered = prev.filter((item) => {
        const existingKey = Object.keys(item)[0];
        const index = Object.values(filterMap).indexOf(existingKey);
        return index < step;
      });

      return [...filtered, { [keyName]: values }];
    });
  };

  /* ---------------------------
     BACK BUTTON
  ---------------------------- */
  const handleBack = () => {
    setCurrentStep((prev) => {
      const newStep = prev - 1;

      setSelections((prevSel) => {
        const updated = { ...prevSel };
        Object.keys(updated).forEach((key) => {
          if (Number(key) > newStep) delete updated[key];
        });
        return updated;
      });

      setSelectedFilter((prev) =>
        prev.filter((item) => {
          const key = Object.keys(item)[0];
          const index = Object.values(filterMap).indexOf(key);
          return index <= newStep;
        })
      );

      return newStep;
    });
  };

  const isSelected = (stepIndex, label) =>
    selections[stepIndex]?.some((item) => item.label === label);

  const validateSelections = () =>
    !steps.some((_, index) => !selections[index]?.length);

  return {
    currentStep,
    setCurrentStep,
    steps,
    selections,
    toggleOption,
    getNextStepData,
    isSelected,
    validateSelections,
    loading,
    error,
    handleBack,
  };
};
export default useFloorFinderData;